import { useEffect, useRef, useState } from 'react';
import type { FundingResponse, FundingRow } from '../../shared/contract';
import { loadWithFallback } from '../lib/api';
import { basePath } from '../lib/paths';
import { fmtPct } from '../lib/format';
import { log } from '../lib/log';
import { useInView } from '../site/motion';
import { prefersReducedMotion } from '../hooks/useReducedMotion';
import fundingSnapshot from '../data/funding.json';

const FUNDING_FALLBACK = fundingSnapshot as unknown as FundingResponse;

const ASKS = [
  'what coins are performing strong against bitcoin',
  'what is funding on SOL right now',
  'which perp is paying shorts the most',
  'what moved the most in the last 24 hours',
  'why did BTC just drop',
  'explain what a liquidation actually is',
] as const;

const BEATS = [
  { kind: 'member', at: 300 },
  { kind: 'typing', at: 1100 },
  { kind: 'bot', at: 2400 },
  { kind: 'member2', at: 3400 },
  { kind: 'typing2', at: 4100 },
  { kind: 'bot2', at: 5300 },
] as const;

type Beat = (typeof BEATS)[number]['kind'];

export function AiBots() {
  const [ref, seen] = useInView<HTMLDivElement>(0.15);
  const [runKey, setRunKey] = useState(0);
  const [shown, setShown] = useState<Set<Beat>>(new Set());
  const [rows, setRows] = useState<FundingRow[]>(() => FUNDING_FALLBACK.rows ?? []);
  const timers = useRef<number[]>([]);

  useEffect(() => {
    const controller = new AbortController();
    let alive = true;
    loadWithFallback<FundingResponse>(basePath('/api/funding'), FUNDING_FALLBACK, controller.signal)
      .then((result) => {
        if (!alive) return;
        if (result.data.rows.length > 0) setRows(result.data.rows);
        log.debug('demo', `ai bot demo rows=${result.data.rows.length} source=${result.data.meta.source}`);
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        log.warn('demo', 'ai bot demo could not load funding', err);
      });
    return () => {
      alive = false;
      controller.abort();
    };
  }, []);

  useEffect(() => {
    for (const t of timers.current) window.clearTimeout(t);
    timers.current = [];

    if (!seen) {
      setShown(new Set());
      return;
    }

    if (prefersReducedMotion()) {
      setShown(new Set(BEATS.map((b) => b.kind)));
      return;
    }

    setShown(new Set());
    for (const beat of BEATS) {
      timers.current.push(
        window.setTimeout(() => {
          setShown((prev) => {
            const next = new Set(prev);
            if (beat.kind === 'bot') next.delete('typing');
            if (beat.kind === 'bot2') next.delete('typing2');
            next.add(beat.kind);
            return next;
          });
        }, beat.at),
      );
    }

    return () => {
      for (const t of timers.current) window.clearTimeout(t);
      timers.current = [];
    };
  }, [seen, runKey]);

  const btc = rows.find((r) => r.base === 'BTC');
  const btcChange = btc?.changePct24h ?? null;
  const outperformers =
    btcChange === null
      ? []
      : rows
          .filter((r) => r.base !== 'BTC' && r.changePct24h !== null)
          .map((r) => ({ base: r.base, edge: (r.changePct24h ?? 0) - btcChange }))
          .filter((r) => r.edge > 0)
          .sort((a, b) => b.edge - a.edge)
          .slice(0, 3);
  const payer = [...rows].sort((a, b) => b.annualisedRate - a.annualisedRate)[0];

  return (
    <div className="mk-case" ref={ref}>
      <div className="mk-case-head">
        <h2 className="mk-case-title">Custom integrated AI bots in your Discord</h2>
        <p className="mk-case-line">
          Ping it in your server and it replies with real data, tuned exactly how you want it and with
          all the information you want it to have.
        </p>
      </div>

      <div className="mk-case-grid">
        <div className="mk-case-flow">
          <p className="ai-lead">Answers any of your community&rsquo;s crypto questions:</p>
          <ul className="ai-asks">
            {ASKS.map((ask) => (
              <li className="ai-ask" key={ask}>
                {ask}
              </li>
            ))}
          </ul>
          <p className="ai-note">
            It reads from the same exchange connections the alerts run on, so an answer is never a guess.
            You decide what it can see and what it is allowed to say.
          </p>
          <button
            className="mk-replay"
            onClick={() => {
              setShown(new Set());
              setRunKey((k) => k + 1);
            }}
          >
            Run it again
          </button>
        </div>

        <div className="mk-case-out">
          <div className="dc" data-shown="true">
            <div className="dc-head">
              <span className="dc-hash" aria-hidden="true">
                #
              </span>
              <span className="dc-channel">trading-floor</span>
            </div>

            <div className="dc-body ai-thread">
              <div className="ai-msg" data-shown={shown.has('member') ? 'true' : 'false'}>
                <img className="dc-avatar" src="/avatars/blue.png" alt="" width={40} height={40} />
                <div className="dc-msg-body">
                  <div className="dc-msg-head">
                    <span className="dc-author">User</span>
                    <span className="dc-time">Today at 21:40</span>
                  </div>
                  <p className="ai-text">
                    <span className="ai-mention">@steamed bot</span> what coins are performing strong against bitcoin
                  </p>
                </div>
              </div>

              {shown.has('typing') ? (
                <div className="ai-typing">
                  <span className="ai-dots" aria-hidden="true">
                    <i />
                    <i />
                    <i />
                  </span>
                  steamed bot is thinking
                </div>
              ) : null}

              <div className="ai-msg" data-shown={shown.has('bot') ? 'true' : 'false'}>
                <img className="dc-avatar" src="/avatars/yellow.png" alt="" width={40} height={40} />
                <div className="dc-msg-body">
                  <div className="dc-msg-head">
                    <span className="dc-author">steamed bot</span>
                    <span className="dc-tag">APP</span>
                    <span className="dc-time">Today at 21:40</span>
                  </div>
                  <p className="ai-text">
                    {outperformers.length > 0 ? (
                      <>
                        Over the last 24 hours{' '}
                        {outperformers.map((o, i) => (
                          <span key={o.base}>
                            {i > 0 ? (i === outperformers.length - 1 ? ' and ' : ', ') : ''}
                            <strong>{o.base}</strong> {fmtPct(o.edge, 1)}
                          </span>
                        ))}{' '}
                        against BTC. BTC itself is {btcChange !== null ? fmtPct(btcChange, 1) : '—'}.
                      </>
                    ) : (
                      <>Nothing is outperforming BTC over the last 24 hours right now.</>
                    )}
                  </p>
                </div>
              </div>

              <div className="ai-msg" data-shown={shown.has('member2') ? 'true' : 'false'}>
                <img className="dc-avatar" src="/avatars/red.png" alt="" width={40} height={40} />
                <div className="dc-msg-body">
                  <div className="dc-msg-head">
                    <span className="dc-author">User</span>
                    <span className="dc-time">Today at 21:41</span>
                  </div>
                  <p className="ai-text">
                    <span className="ai-mention">@steamed bot</span> which perp is paying shorts the most
                  </p>
                </div>
              </div>

              {shown.has('typing2') ? (
                <div className="ai-typing">
                  <span className="ai-dots" aria-hidden="true">
                    <i />
                    <i />
                    <i />
                  </span>
                  steamed bot is thinking
                </div>
              ) : null}

              <div className="ai-msg" data-shown={shown.has('bot2') ? 'true' : 'false'}>
                <img className="dc-avatar" src="/avatars/yellow.png" alt="" width={40} height={40} />
                <div className="dc-msg-body">
                  <div className="dc-msg-head">
                    <span className="dc-author">steamed bot</span>
                    <span className="dc-tag">APP</span>
                    <span className="dc-time">Today at 21:41</span>
                  </div>
                  <p className="ai-text">
                    {payer ? payer.base : 'SOL'} at {payer ? fmtPct(payer.annualisedRate * 100, 1) : '—'}{' '}
                    annualised. Short it and you collect that, minus fees, for as long as it holds.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
